import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewEncapsulation} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {select, Store} from '@ngrx/store';
import {take, takeUntil} from 'rxjs/operators';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {Configuration} from '@shared/models/configuration';
import {CommandService} from '@core/services/command.service';
import {FileManagementService} from '@core/services/file-management.service';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {DownloadFileCommand} from '../../../file-manager/commands/create-directory.command';
import {FileDialogComponent} from '../../../file-manager/component/file-dialog/file-dialog.component';
import {AddAttachmentCommand, RemoveAttachmentCommand} from '../../commands/task-manager.command';
import {TaskManagerState} from '../../store/task-manager.reducer';
import {GraphqlContentScope} from '@shared/models/graphql-content-scope.model';
import {DialogService} from '@shared/services/dialog-service';
import {DialogModel} from '@shared/models/dialog.model';
import {SetTempAccessTokenAction} from '@shared/store/actions/auth.actions';
import {SetDialogAttachmentAction} from '../../store/task-manager.action';
import {Router} from '@angular/router';
import {SnackBarService} from '@core/services/snack-bar.service';
import {TaskManagerConfig} from '@features/task-manager/helpers/task-manager.config';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {hasFeature} from '@shared/store/selectors/security.selector';
import {TicketAssignee, TicketManager, TicketOwner} from '../../constants/task-manager.constants';
import {PlatformQuery} from '@shared/models/platform-query/platform-query';
import {PlatformQueryService} from '@core/services/platform-query.service';
import {PlatformCommand} from '@shared/commands/command-base';
import { UploadFileStructure, ViewType } from '@features/task-manager/models/task-manager.model';
import { Guid } from 'guid-typescript';
import { RootFolderPath } from '@shared/constants/RelativePath';
import { FileStoreCommonService } from '@features/file-manager/services/file-store-common.service';
import {
  anyTicketAttachmentScanning,
  ticketAttachmentChip,
  ticketAttachmentChipLabel,
  TicketAttachmentChip
} from './ticket-attachment-scan';


@Component({
    standalone: false,
    selector: 'particle-task-attachments',
  templateUrl: './task-attachments.component.html',
  styleUrls: ['./task-attachments.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TaskAttachmentsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() cardId: string;
  sourceKey: string;
  isClose = false;
  invalidateSubject: InvalidateSubject;
  @Input() dynamicData: any;
  scope: number;
  hasFeature$: Observable<any>;
  hasAddAttachmentPermission: boolean;
  hasDeleteAttachmentPermission: boolean;
  attachments = [];
  ticket: any;
  rootFolderPath: string;
  @Input() data: DialogModel;
  @Input() openAddAttachmentDialog$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private ngUnsubscribe = new Subject<void>();
  api = 'TaskManagerApi';
  allAttachmentCommands: AddAttachmentCommand[] = [];
  /** ITP-E-179-03 — how often the list is re-read while an attachment's malware scan runs. */
  static readonly scanPollMs = 5000;
  private scanPoll: ReturnType<typeof setTimeout> | null = null;
  constructor(protected dialog: MatDialog,
              private taskManagerStore: Store<TaskManagerState>,
              private fileStoreService: FileStoreCommonService,
              protected commandService: CommandService,
              protected fileManagementService: FileManagementService,
              protected dialogService: DialogService,
              private router: Router,
              private securityStore: Store<SecurityState>,
              private snackbarService: SnackBarService,
              private platformQueryService: PlatformQueryService
  ) {
    this.sourceKey = 'TaskAttachment';
    this.invalidateSubject = new InvalidateSubject();
  }

  ngOnInit() {
    if (this.data.input.parameters?.taskApi) {
      this.api = this.data.input.parameters?.taskApi;
    }
    this.hasFeature$ = this.securityStore.pipe(select(hasFeature), takeUntil(this.ngUnsubscribe));
    this.scope = this.dynamicData.board.scopeInt ? this.dynamicData.board.scopeInt : GraphqlContentScope.getNumberScope(this.dynamicData.board.scope);
    this.cardId = this.dynamicData.card.id;
    const config = Configuration.getSmartGridConfig(this.sourceKey);
    config.Parameters.TICKET_ID = this.cardId;
    config.Parameters.SCOPE = this.scope;
    this.hasAddAttachmentPermission = this.hasPermissions();
    this.hasDeleteAttachmentPermission = this.hasDeletePermission();
    const query = new PlatformQuery();
    query.name = 'TaskQuery';
    query.api = this.api;
    query.fieldValues['TaskId'] = this.cardId;
    query.fieldValues['Scope'] = this.scope;
    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {
      this.ticket = response.fieldValues['KanbanTicket'];
      this.rootFolderPath = this.ticket.fieldValues['RootFolderPath'];
      console.log('RootFolderPath', this.rootFolderPath);
    });
    this.getAttachmentsInfo();
    this.openAddAttachmentDialog$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(open => {
      if (open) {
        this.showAddAttachmentDialog();
        this.scrollAddAttachmentIntoView();
      }
  });
  }

  ngOnDestroy(): void {
    if (this.scanPoll) {
      clearTimeout(this.scanPoll);
    }
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.directUploadFromComponent();
  }


  private directUploadFromComponent() {
    if (this.data.input?.isDirectUpload && this.data.input?.selectedItem) {
      let selectedItem = this.data.input?.selectedItem;
      if(!Array.isArray(selectedItem)){
        selectedItem = [selectedItem];
      }
      selectedItem.forEach(item => {
        this.setAddAttachmentsCommands(item);
      });
      this.executeAddAttachmentCommand();
    }
  }

  showFileDialog() {
    const dialogInput = {
      minWidth: '680px',
      maxWidth: '680px',
      data: {
        itemsUrlIndex: 1,
        type: 'SharedDrives',
        folderId: '',
        driveId: '',
        isFileSelector: true
      }
    };
    if (this.rootFolderPath) {
      dialogInput.data['viewType'] = 'MyComputer';
      dialogInput.data['rootFolderPath'] = { value: this.rootFolderPath };
    }

    const dialogRef = this.dialog.open(FileDialogComponent, dialogInput);

    dialogRef.afterClosed().pipe(takeUntil(this.ngUnsubscribe)).subscribe((result: any) => {
      if (result) {
        const addAttachmentCommand = new AddAttachmentCommand(this.cardId, result.id, result.cells.Name.displayText, 'FileEntry', 3, result['ViewType']);
        addAttachmentCommand.scope = this.scope;
        const platformCommand = new PlatformCommand('AddAtachmentPlatformCommand', this.api);
        platformCommand.fieldValues = {
          AddAttachmentCommand: addAttachmentCommand
        };
        this.commandService.executePlatformCommand(platformCommand).pipe().subscribe(data => {
          this.invalidateSubject.invalidate();
        });
      }
    });
  }

  showAddAttachmentDialog() {
    const dialogInput = {
      minWidth: '680px',
      maxWidth: '680px',
      data: {
        itemsUrlIndex: 1,
        type: 'SharedDrives',
        folderId: '',
        driveId: '',
        isFileSelector: true,
        multipleUploadEnabled: true,
        directUploadEnabled: true,
        maxUploadSizeInBytes: TaskManagerConfig.maxAttachmentSizeInBytes,
        scope: this.scope,
        cardId: this.cardId
      }
    };

    if(this.data.input?.board?.id == "orbitax_support"){
      dialogInput.data['viewType'] = "MyComputer";
    }
    if (this.rootFolderPath) {
      dialogInput.data['viewType'] = 'MyComputer';
      dialogInput.data['rootFolderPath'] = { value: this.rootFolderPath };
    }else{
      dialogInput.data['rootFolderPath'] = {value : RootFolderPath.TaskRootFolderPath};
    }

    const dialogRef = this.dialog.open(FileDialogComponent, dialogInput);

    //requirement itp2-636: allow multiple attachment
    dialogRef.afterClosed().pipe(takeUntil(this.ngUnsubscribe)).subscribe((result: UploadFileStructure[]) => {
      if (result) {
        if (!Array.isArray(result)) {
          result = [result];
        }
        result.forEach(item => {
          this.setAddAttachmentsCommands(item);
        });
        this.executeAddAttachmentCommand();
      }
    });
  }

  private setAddAttachmentsCommands(uploadedFile: UploadFileStructure) {
    const addAttachmentCommand = new AddAttachmentCommand(this.cardId, uploadedFile.id, uploadedFile.cells.Name.displayText, 'FileEntry', this.scope, uploadedFile['ViewType']);
    // addAttachmentCommand.scope = this.scope;
    this.allAttachmentCommands.push(addAttachmentCommand);
  }

  private executeAddAttachmentCommand() {
    const multipleAttachmentCommand = new AddAttachmentCommand(this.cardId, Guid.create().toString(), 'Multiple upload Attachment', 'FileEntry', this.scope, ViewType.MyComputer);
    multipleAttachmentCommand.attachmentCommands = [...this.allAttachmentCommands];
    const platformCommand = new PlatformCommand('AddAtachmentPlatformCommand', this.api);
    platformCommand.fieldValues = {
      AddAttachmentCommand: multipleAttachmentCommand
    };
    this.commandService.executePlatformCommand(platformCommand).pipe().subscribe(data => {
      this.getAttachmentsInfo();
      this.scrollAddAttachmentIntoView();
      this.allAttachmentCommands = []; //reset
    });
  }

  onGridCellValueChange($event) {
    console.log($event);
    if ($event.action) {
      switch ($event.action.id) {
        case 'Open':
        case 'Download':
          this.downloadAttachment($event.item);
          break;
        // Will handle in nov release
        // case 'Open':
        //   this.openAttachment($event.item);
        //   break;
        case 'Delete':
          this.deleteAttachment($event.item.id);
          break;
      }
    } else {
      this.downloadAttachment($event.item);
      // Will handle in nov release
      // this.openAttachment($event.item);
    }
  }

  // deleteAttachment(id) {
  //   const removeAttachmentCommand = new RemoveAttachmentCommand(this.cardId, [id], this.scope);
  //   this.commandService.executeCommand(removeAttachmentCommand).pipe().subscribe(data => {
  //     console.log('Removed Attachments', data);
  //     this.snackbarService.showInformation('Attachment removed successfully!!!');
  //     this.invalidateSubject.invalidate();
  //   }, error => {
  //     console.log('Removed File', error);
  //   });
  // }

  deleteAttachment(attachment) {
    if (!this.hasAddAttachmentPermission) return;
    // new RemoveAttachmentCommand(this.cardId, [attachment.id], this.scope);
    const removeAttachmentCommand = new PlatformCommand('RemoveAttachmentCommand', this.api);
    removeAttachmentCommand.fieldValues = {
      TicketId: this.cardId,
      AttachmentIds: [attachment.id],
    };
    removeAttachmentCommand.scope = this.scope;
    this.commandService.executePlatformCommand(removeAttachmentCommand).pipe().subscribe(data => {
      console.log('Removed Attachments', data);
      this.snackbarService.showInformation('Attachment removed successfully!!!');
      this.getAttachmentsInfo();
    }, error => {
      console.log('Removed File', error);
    });
  }


  // downloadAttachment(event) {
  //   const downloadFileCommand = new DownloadFileCommand(event.id, '', true);
  //   downloadFileCommand.ticketId = this.cardId;
  //   downloadFileCommand.scope = this.scope;
  //   downloadFileCommand.fileName = event.cells['Title'].value;
  //   if (event.cells['IsGlobalFile'] && event.cells['IsGlobalFile'].value) {
  //     downloadFileCommand.isClientFile = !event.cells['IsGlobalFile'].value;
  //   }
  //   this.commandService.executeCommand(downloadFileCommand).pipe().subscribe(data => {
  //     console.log('Download File', data);
  //     if (data.Payload['DownloadUrl']) {
  //       this.fileManagementService.downloadOneTimeSignedUrl(data.Payload['DownloadUrl'], event.cells['Title'].displayText);
  //       if (event.id) {
  //         this.sendDownloadAttachmentActivityCommand(data, event.id);
  //       }
  //     } else if (data.Payload['Type']) {
  //       const accessToken = data.Payload['AccessToken'];
  //       this.taskManagerStore.dispatch(new SetTempAccessTokenAction(accessToken));
  //       const attachment = data.Payload['Payload'];
  //       this.taskManagerStore.dispatch(new SetDialogAttachmentAction({attachment: attachment}));
  //       if (attachment.Url && attachment.Url.length) {
  //         this.dialogService.closeDialog({'url': attachment.Url, 'action': 'Redirect'});
  //         this.router.navigate([`${attachment.Url}`], {
  //           queryParams: {
  //             boardId: this.dynamicData.board.id,
  //             ticketId: this.dynamicData.card.id
  //           }
  //         });
  //       } else if (attachment.Editor && attachment.Editor.length) {
  //         this.showAttachmentDialog(attachment);
  //       }
  //     }
  //   });
  // }
  /** ITP-E-179-03 — the attachment's malware scan chip: Scanning…, Blocked, or none once it may be downloaded. */
  chipOf(attachment): TicketAttachmentChip {
    return ticketAttachmentChip(attachment);
  }

  chipLabel(attachment): string {
    return ticketAttachmentChipLabel(this.chipOf(attachment));
  }

  public clickAttachmentPointer(currentAttachment)
  {
    // ITP-E-179-01/03 — the server refuses a file that is not Clean; say why here instead of failing the download.
    if (this.chipOf(currentAttachment) !== 'ready') {
      this.snackbarService.showInformation(this.chipOf(currentAttachment) === 'blocked'
        ? 'Blocked: threat found. This file cannot be downloaded.'
        : 'Scanning… This file can be downloaded once its malware scan is finished.');
      return;
    }
    if(currentAttachment.AttachmentType != undefined && currentAttachment.AttachmentType == 'Folder')
    {
      return this.navigateToAttachmentFolder(currentAttachment);
    }
    return this.downloadAttachment(currentAttachment);
  }

  downloadAttachment(currentAttachment) {
    const downloadFileCommand = new DownloadFileCommand(currentAttachment.id, '', true);
    downloadFileCommand.ticketId = this.cardId;
    downloadFileCommand.scope = this.scope;
    downloadFileCommand.fileName = currentAttachment['Title'];
    downloadFileCommand.taskApi = this.api;
    if (currentAttachment['IsGlobalFile']) {
      downloadFileCommand.isClientFile = !currentAttachment['IsGlobalFile'];
    }
    this.commandService.executeCommand(downloadFileCommand).pipe().subscribe(data => {
      console.log('Download File', data);
      if (data.Payload['DownloadUrl']) {
        this.fileManagementService.downloadOneTimeSignedUrl(data.Payload['DownloadUrl'], currentAttachment['DisplayTitle']);
        if (currentAttachment.id) {
          this.sendDownloadAttachmentActivityCommand(data, currentAttachment.id);
        }
      } else if (data.Payload['Type']) {
        const accessToken = data.Payload['AccessToken'];
        this.taskManagerStore.dispatch(new SetTempAccessTokenAction(accessToken));
        const attachment = data.Payload['Payload'];
        this.taskManagerStore.dispatch(new SetDialogAttachmentAction({attachment: attachment}));
        if (attachment.Url && attachment.Url.length) {
          this.dialogService.closeDialog({'url': attachment.Url, 'action': 'Redirect'});
          this.router.navigate([`${attachment.Url}`], {
            queryParams: {
              boardId: this.dynamicData.board.id,
              ticketId: this.dynamicData.card.id
            }
          });
        } else if (attachment.Editor && attachment.Editor.length) {
          this.showAttachmentDialog(attachment);
        }
      }
    });
  }

  public navigateToAttachmentFolder(currentAttachment)
  {
    //i should have a relative path prop in the currentAttachment
    var relativePath = currentAttachment.RelativePath;
    if(relativePath == undefined || relativePath == null)
    {
      // if no relative path lets go try to download
      return this.downloadAttachment(currentAttachment);
    }

    this.fileStoreService.navigateToClientFolderUsingRelativepath(relativePath);
  }

  sendDownloadAttachmentActivityCommand(data, attachmentId) {
    const downLoadAttachmentActivityCommand = new PlatformCommand('DownLoadAttachmentActivityCommand', 'TaskManagerApi');
    downLoadAttachmentActivityCommand.fieldValues['FileId'] = attachmentId;
    downLoadAttachmentActivityCommand.fieldValues['FileName'] = data.Payload['FileName'];
    downLoadAttachmentActivityCommand.fieldValues['TaskId'] = this.ticket.id;
    downLoadAttachmentActivityCommand.scope = this.ticket.scope;
    this.commandService.executePlatformCommand(downLoadAttachmentActivityCommand).pipe(take(1)).subscribe(response => {
        console.log(response);
      },
      error => {
        console.error(error);
      });
  }

  openAttachment(event) {
    const command = new PlatformCommand('OpenFileCommand', 'FileStoreApi');
    if (this.data.input.parameters && this.data.input.parameters['command']) {
      const originatingCommand = this.data.input.parameters['command'];
      command.fieldValues = originatingCommand.fieldValues;
    }
    command.fieldValues['IsClientFile'] = true;
    command.fieldValues['IsClientDrive'] = true;
    command.fieldValues['TicketId'] = this.cardId;
    command.fieldValues['BoardScope'] = this.scope;
    command.fieldValues['ItemId'] = event.id;
    if (event.cells['IsGlobalFile'] && event.cells['IsGlobalFile'].value) {
      command.fieldValues['IsClientFile'] = !event.cells['IsGlobalFile'].value;
      command.fieldValues['IsClientDrive'] = !event.cells['IsGlobalFile'].value;
    }
    this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(response => {
      if (response.fieldValues['DownloadUrl']) {
        this.fileManagementService.downloadOneTimeSignedUrl(response.fieldValues['DownloadUrl'], event.cells['Title'].displayText);
      }
      if (response.fieldValues['RedirectUrl']) {
        this.data.dialogRef.close({
          action: 'Cancel',
          data: this.data
        });
        this.router.navigateByUrl(response.fieldValues['RedirectUrl']);
        return;
      }
    });
  }

  onSelectionChanged($event) {

  }

  close() {
    this.isClose = true;
  }

  private showAttachmentDialog(attachment: any) {
    const dialogModel = DialogModel.create(attachment['AttachmentTitle'], attachment.Editor, attachment
      , '800px');
    dialogModel.isFullscreen = true;
    this.dialogService.showOkCancelDialog(dialogModel).subscribe(state => {
      if (state && state.action === 'OK') {
        this.taskManagerStore.dispatch(new SetTempAccessTokenAction(null));
      }
    });
  }

  getAttachmentsInfo(){
    if(this.dynamicData && this.dynamicData.card){
      let getTaskAttachmentsQuery = this.getTaskAttachementsQuery();
      this.platformQueryService.executeQuery(getTaskAttachmentsQuery).pipe(take(1)).subscribe(response => {
        if(response && response.fieldValues && response.fieldValues['Attachments']){
          let currentAttachments = response.fieldValues['Attachments'];
          this.attachments = [];
          currentAttachments.forEach(element => {
             let attachment = element.fieldValues;
             attachment.id = element.id;
             this.attachments.push(attachment);
          });
          this.pollWhileScanning();
        }
      });
    }
  }

  // ITP-E-179-03 — a scan verdict is not pushed to the list: re-read it while a file is still being scanned.
  private pollWhileScanning() {
    if (this.scanPoll) {
      clearTimeout(this.scanPoll);
      this.scanPoll = null;
    }
    if (anyTicketAttachmentScanning(this.attachments)) {
      this.scanPoll = setTimeout(() => {
        this.scanPoll = null;
        this.getAttachmentsInfo();
      }, TaskAttachmentsComponent.scanPollMs);
    }
  }

  private getTaskAttachementsQuery() {
    let getTaskAttachementsQuery = new PlatformQuery();
    getTaskAttachementsQuery.api = this.api;
    getTaskAttachementsQuery.name = 'TaskAttachmentsQuery';
    getTaskAttachementsQuery.fieldValues['TaskId'] = this.dynamicData.card.id;
    getTaskAttachementsQuery.fieldValues['Scope'] = this.dynamicData.card.scopeInt;
    getTaskAttachementsQuery.isCamelCase = true;
    return getTaskAttachementsQuery;
  }

  private hasPermissions(): boolean {
    const permissionType = this.dynamicData.card.permissionLevel;
    return (TicketOwner <= permissionType && permissionType <= TicketAssignee);
  }
  private hasDeletePermission(): boolean {
    const permissionType = this.dynamicData.card.permissionLevel;
    return (TicketOwner <= permissionType && permissionType <= TicketManager);
  }
  private scrollAddAttachmentIntoView() {
    const element = document.getElementById('task-attachment-dialog-box');
    if (element) {
      element.scrollIntoView(false);
    }
  }

  public getAttachmentClass(type: string | undefined)
  {
    return [
      this.getPointerClass(type)
    ]
  }

  private getPointerClass(type: string | undefined){
    // TODO: create a object or enum for the the Type
    if(type && type == "Folder") return 'fa-solid fa-arrow-up-right-from-square';
    else return 'fa-solid fa-arrow-to-bottom';
  }
}
